import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { armazenamento } from './armazenamento.ts';
import type { EstadoDoProjecto, Projectos } from './percursos.ts';

/**
 * E12 — as entregas do estudante ao projecto integrador (H-124), do lado do
 * cliente.
 *
 * O estado de cada projecto vem do servidor (`GET /api/projectos`) e fica no
 * aparelho, como o resto: em modo de avião o percurso mostra a última entrega
 * conhecida, e a entrega nova só se faz com ligação — não há fila offline
 * para isto, de propósito. Uma entrega é uma acção rara, com um artefacto
 * que se quer confirmar entregue; sincronizá-la mais tarde e em silêncio
 * seria mentir «entregue» a quem está a olhar.
 *
 * Só chega ao pacote de quem abre o percurso: este ficheiro é importado
 * pela rota lazy `/percurso/$pathId`, nunca pela casca.
 */

export interface Entrega {
  projecto: string;
  estado: EstadoDoProjecto;
  versao: number;
  submetidoEm: string;
  revistoEm: string | null;
  feedback: string | null;
  url: string | null;
  texto: string | null;
}

interface EstadoDeProjectos {
  /** A última versão de cada projecto, por slug. */
  entregas: Record<string, Entrega>;
  /** Quando o servidor foi ouvido pela última vez; `null` nunca. */
  actualizadoEm: string | null;
  substituir: (entregas: Entrega[]) => void;
  limpar: () => void;
}

export const useProjectos = create<EstadoDeProjectos>()(
  persist(
    (set) => ({
      entregas: {},
      actualizadoEm: null,
      substituir: (lista) =>
        set({
          entregas: Object.fromEntries(lista.map((e) => [e.projecto, e])),
          actualizadoEm: new Date().toISOString(),
        }),
      limpar: () => set({ entregas: {}, actualizadoEm: null }),
    }),
    { name: 'projectos', storage: armazenamento() },
  ),
);

/** O mapa slug → estado que `src/state/percursos.ts` consome. */
export function estadosDosProjectos(entregas: Record<string, Entrega>): Projectos {
  return new Map(Object.entries(entregas).map(([slug, e]) => [slug, e.estado]));
}

export class ErroDeEntrega extends Error {
  constructor(
    public readonly codigo: number,
    mensagem: string,
  ) {
    super(mensagem);
  }
}

async function ler(resposta: Response): Promise<Entrega[]> {
  if (!resposta.ok) {
    let mensagem = '';
    try {
      mensagem = ((await resposta.json()) as { erro?: string }).erro ?? '';
    } catch {
      /* corpo sem JSON — a mensagem fica vazia e o código chega */
    }
    throw new ErroDeEntrega(resposta.status, mensagem);
  }
  const dados = (await resposta.json()) as { entregas: Entrega[] };
  return dados.entregas ?? [];
}

/** Ouve o servidor e substitui o que está no aparelho. Sem sessão, 401 sobe. */
export async function carregarProjectos(signal?: AbortSignal): Promise<void> {
  const r = await fetch('/api/projectos', { credentials: 'same-origin', signal });
  useProjectos.getState().substituir(await ler(r));
}

/** Entrega uma versão nova. O servidor devolve a lista já com ela. */
export async function entregarProjecto(dados: {
  projecto: string;
  url?: string;
  texto?: string;
}): Promise<void> {
  const r = await fetch('/api/projectos', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(dados),
  });
  useProjectos.getState().substituir(await ler(r));
}
